import { Layout } from '@community_dev/pixels'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import { Card, Descriptions, Empty, Tag, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import type { JSX } from 'react'

import { CommunityRegistration, patchCommunityRegistration } from 'api/tcr'
import { QUERIES } from 'constants/queries'
import { Switch } from 'shared/components/Switch'

const SMS_STATUS_TOOLTIP =
  "This status is calculated based on the current state of the client's registration and billing activation. Other parts of the system may also affect whether a client can send SMS."

type RegistrationDataProps = {
  canWrite?: boolean
  clientId: string
  disabledTooltip?: string
  registration: CommunityRegistration | undefined
}

export function RegistrationData({
  canWrite = true,
  clientId,
  disabledTooltip,
  registration,
}: RegistrationDataProps): JSX.Element {
  const queryClient = useQueryClient()
  return (
    <Card title="Registration">
      {registration ? (
        <Descriptions bordered column={1} labelStyle={{ width: 200 }} size="small">
          <Descriptions.Item label="Exempt">
            <Switch
              defaultChecked={registration.isExempt}
              disabled={!canWrite}
              name="Exempt"
              onSuccess={() => queryClient.invalidateQueries([QUERIES.TCR.COMMUNITY_REGISTRATIONS, clientId])}
              prepare={(value) => ({
                clientId,
                isExempt: value,
              })}
              tooltip={!canWrite ? disabledTooltip : undefined}
              updateEntity={patchCommunityRegistration}
            />
          </Descriptions.Item>
          <Descriptions.Item label="TCR Status">
            <Tag>{registration.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <Layout alignItems="center" display="flex" gap="8px">
                <span>SMS Status</span>
                <Tooltip title={<Typography.Text style={{ color: 'white' }}>{SMS_STATUS_TOOLTIP}</Typography.Text>}>
                  <FontAwesomeIcon icon="info-circle" />
                </Tooltip>
              </Layout>
            }
          >
            {registration.client.calculatedSmsStatus ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Share Status">
            {registration.campaign?.campaignShareStatus?.status ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="DCA Completed At">
            {registration.campaign?.dcaCompletedAt
              ? dayjs(registration.campaign.dcaCompletedAt).format('MM/DD/YYYY hh:mm A')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Share Accepted At">
            {registration.campaign?.campaignShareStatus?.shareAcceptedAt
              ? dayjs(registration.campaign.campaignShareStatus.shareAcceptedAt).format('MM/DD/YYYY hh:mm A')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="TCR Campaign Link">
            {registration.campaign?.tcrCampaignLink ? (
              <a href={registration.campaign.tcrCampaignLink} rel="noreferrer noopener" target="_blank">
                {registration.campaign.tcrCampaignLink}
              </a>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="TCR Brand Link">
            {registration.brand?.tcrBrandLink ? (
              <a href={registration.brand.tcrBrandLink} rel="noreferrer noopener" target="_blank">
                {registration.brand.tcrBrandLink}
              </a>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="CNP">{registration.campaign?.campaignShareStatus?.cnp ?? '-'}</Descriptions.Item>
        </Descriptions>
      ) : (
        <Empty description="No registration data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Card>
  )
}
